import { useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { createLoadOrder } from "../../../actions/order";
import { HYDRATE_CATALOG_ITEM } from "../../../actions/item";
import {
    CatalogHydrationRootState,
    getItemDetails,
    selectCatalogOrder,
    selectIsCatalogItemHydrated,
} from "../../../redux/catalog";

type HydrationResult =
    | { ok: true; item: Record<string, unknown> }
    | { ok: false; item: null };

async function hydrateCatalogItem(
    dispatch: any,
    getState: () => CatalogHydrationRootState,
    catalogId: string,
    itemId: string,
    forceRefresh: boolean,
): Promise<HydrationResult> {
    const readState = () => getState() as CatalogHydrationRootState;
    let state = readState();

    // Edit flow: once hydrated, trust Redux and skip the network round-trip.
    // Upgrade flow passes forceRefresh so it always gets current supplier/pricing data.
    if (!forceRefresh && selectIsCatalogItemHydrated(state, itemId)) {
        return { ok: true, item: state.entities.items[itemId] ?? {} };
    }

    if (!state.entities.orders[catalogId]?.loaded) {
        await dispatch(createLoadOrder(catalogId));
    }

    state = readState();
    const existingItem = state.entities.items[itemId] ?? {};
    const result = await (dispatch as any)(getItemDetails(itemId));

    if (!getItemDetails.fulfilled.match(result) || !result.payload) {
        return { ok: false, item: null };
    }

    const item = { ...existingItem, ...result.payload } as Record<string, unknown>;

    state = readState();
    dispatch({
        type: HYDRATE_CATALOG_ITEM,
        payload: {
            item,
            order: {
                ...(state.entities.orders[catalogId] ?? {}),
                order_id: catalogId,
                loaded: true,
            },
            items: [item],
            artworks: item.artworks ?? [],
            parent_type: item.parent_type ?? "PRODUCT",
            parent_id: item.parent_id,
        },
    });

    return { ok: true, item };
}

export function useCatalogItemHydration(
    catalogId: string,
    itemId: string,
    { forceRefresh = false }: { forceRefresh?: boolean } = {},
) {
    const dispatch = useDispatch();
    const store = useStore();
    const [item, setItem] = useState<Record<string, unknown> | null>(null);
    const [failed, setFailed] = useState(false);

    const ready = useSelector((state: CatalogHydrationRootState) =>
        selectIsCatalogItemHydrated(state, itemId),
    );
    const order = useSelector((state: CatalogHydrationRootState) =>
        selectCatalogOrder(state, catalogId, itemId),
    );

    useEffect(() => {
        if (!catalogId || !itemId) return;
        let cancelled = false;
        setItem(null);
        setFailed(false);
        hydrateCatalogItem(
            dispatch as any,
            store.getState as () => CatalogHydrationRootState,
            catalogId,
            itemId,
            forceRefresh,
        ).then((result) => {
            if (cancelled) return;
            if (result.ok) setItem(result.item);
            else setFailed(true);
        });
        return () => { cancelled = true; };
    }, [catalogId, itemId, dispatch, store, forceRefresh]);

    return { ready, order, item, failed };
}
